import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GENERAL_COMPLIANCE, ErrorMessages } from '../../../../shared/constants/constants';
import { PersonaType, RoutesEnum, VendorProfile, vendorProfileMetrics } from '@app/shared/constants/global.enum';
import { GlobalErrorService } from '@services/global-error.service';
import {
  MonthOption,
  PEOPLE_METRICS,
} from '@constants/people-metrics.constants';
import { ToasterService } from '@services/toaster.service';
import { Subject, takeUntil } from 'rxjs';
import { QuarterlyFeedbackComplianceService } from '@services/quarterly-feedback-compliance.service';
import { GeneralComplianceResponseData, GeneralComplianceResponse } from '@interfaces/quarterly-feedback.model';
import { ActivatedRoute } from '@angular/router';
import { generateQuarterOptions, getCurrentYear } from '@utils/utils';
import {
  ToastTitle,
} from '@app/shared/constants/global.enum';
import { GeneralComplianceLabels } from '@app/shared/constants/global.enum'; // <-- Add this import

export interface IEmeraldTabData {
  label: string;
}

type GeneralComplianceCard = {
  label: string;
  value: string | number;
  highlight: boolean;
  fileUrl: string;
};

@Component({
  selector: 'app-quarterly-feedback',
  templateUrl: './quarterly-feedback.component.html',
  styleUrls: ['./quarterly-feedback.component.scss'],
})

export class QuarterlyFeedbackComponent implements OnInit, OnDestroy {
  VendorProfile = VendorProfile;
  tabsData: IEmeraldTabData[] = [
    { label: vendorProfileMetrics.compliance },
    { label: vendorProfileMetrics.peopleMetrics },
    { label: vendorProfileMetrics.learningDevelopment },
    { label: vendorProfileMetrics.strategicPartnership },
  ];

  selectedTab: IEmeraldTabData = this.tabsData[0];
  selectedQuarter: string = '';
  selectedYear: string = '';
  data: null = null;
  monthOptions: {
    value: string;
    label: string;
    month: string;
    year: string;
    quarter: string;
    selected?: boolean;
  }[] = [];
  preSelectedValue: string = '';
  isLoading: boolean = false;
  selectedMetricsMonth: number = 1;
  metricsMonth: MonthOption[] = [];
  metricsYear: number = new Date().getFullYear();
  private readonly destroy$ = new Subject<void>();
  RoutesEnum = RoutesEnum;
  personaType = RoutesEnum.OpsVendorManagement;
  fieldVisibility = PersonaType.OpsPersona;
  generalComplianceCards: GeneralComplianceCard[] = GENERAL_COMPLIANCE.map(
    (c) => ({ ...c }),
  );
  
  @Input() vendorId: string | null = null;

  constructor(
    private errorHandler: GlobalErrorService,
    private route: ActivatedRoute,
    private toasterService: ToasterService,
    private quarterlyFeedbackComplianceService: QuarterlyFeedbackComplianceService,
  ) { }

  onTabChange(tab: any) {
    this.selectedTab = { ...tab, label: tab.label ?? '' };
  }

  tabLabels = {
    Compliance: vendorProfileMetrics.compliance,
    PeopleMetrics: vendorProfileMetrics.peopleMetrics,
    LearningDevelopment: vendorProfileMetrics.learningDevelopment,
    StrategicPartnership: vendorProfileMetrics.strategicPartnership,
  };

  ngOnInit(): void {
    const currentYear = parseInt(getCurrentYear());
    this.monthOptions = generateQuarterOptions(currentYear, 1);
    const preSelectedOption = this.monthOptions.find(
      (option) => option.selected,
    );
    if (preSelectedOption) {
      this.selectedQuarter = preSelectedOption.quarter;
      this.selectedYear = preSelectedOption.year;
      this.preSelectedValue = preSelectedOption.value;
    }
    this.loadGeneralComplianceData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQuarterChange(event: string): void {
    const selectedOption = this.monthOptions?.find(option => option.label === event);

    if (!selectedOption) {
      this.errorHandler.showError(this.VendorProfile.invalidMonthSelected);
      return;
    }

    this.selectedQuarter = selectedOption.quarter;
    this.selectedYear = selectedOption.year;
    this.preSelectedValue = selectedOption.value;

    this.loadGeneralComplianceData();
  }

  private getParamValue(params: any, keys: string[]): string {
    for (const key of keys) {
      if (params?.[key]) {
        return params[key];
      }
    }
    return '';
  }

  private loadGeneralComplianceData(): void {
    if (!this.vendorId) {
      this.toasterService.showError(
        PEOPLE_METRICS.MESSAGES.MISSING_IDS_ERROR,
        ToastTitle.ERROR,
      );
      return;
    }

    this.isLoading = true;
    this.quarterlyFeedbackComplianceService
      .getGeneralCompliance(
        this.metricsYear.toString(),
        Number(this.vendorId),
        this.selectedQuarter,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
            const data: GeneralComplianceResponse[] = Array.isArray(res?.data) ? res?.data : [res?.data].filter(Boolean);

          this.generalComplianceCards = this.buildGeneralComplianceCards(data);
        },
        error: () => {
          this.showError(ErrorMessages.NoDataAvailable);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  downloadFile(url: string, filename: string) {
    if(url){
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    }
  }

  private buildGeneralComplianceCards(
    data: GeneralComplianceResponse[],
  ): GeneralComplianceCard[] {
    const totalRecords = (data ?? []).length;
    const base = GENERAL_COMPLIANCE.map((c) => ({ ...c }));


    const complianceData = data[0] || {};
    const invoiceComplianceUrl = complianceData?.invoiceComplianceUrl || '';
    const msaRateCardComplianceUrl = complianceData?.msaRateCardComplianceUrl || '';
    const assetComplianceUrl = complianceData?.assestComplianceUrl || '';
    const sowComplianceUrl = complianceData?.sowComplianceUrl || '';

    const invoiceCompliance = complianceData?.invoiceCompliance || 0;
    const msaRateCardCompliance = complianceData?.msaRateCardCompliance || 0;
    const assetCompliance = complianceData?.assestCompliance || 0;
    const sowCompliance = complianceData?.sowCompliance || 0;

    for (const card of base) {
      switch (card.label) {
        case GeneralComplianceLabels.MSA_RATE_CARD_EXCEPTIONS:
          card.value = msaRateCardCompliance;
          card.fileUrl = msaRateCardComplianceUrl;
          break;
        case GeneralComplianceLabels.SOW_COMPLIANCE:
          card.value = sowCompliance;
          card.fileUrl = sowComplianceUrl;
          break;
        case GeneralComplianceLabels.INVOICE_COMPLIANCE:
          card.value = invoiceCompliance;
          card.fileUrl = invoiceComplianceUrl;
          break;
        case GeneralComplianceLabels.ASSET_COMPLIANCE:
          card.value = assetCompliance;
          card.fileUrl = assetComplianceUrl;
          break;
        default:
          card.value = 0;
          break;
      }
    }
    return base;
  }

  private showError(message: string): void {
    this.toasterService.showError(message, ToastTitle.ERROR);
  }
}
